import * as angular from 'angular';
import { FieldMetadata } from '../../directives/EditableContent/models';
import { IMetadataValidationService } from '../../typings/services';

export class MetadataValidationService implements IMetadataValidationService {
    public static $name: string = 'metadataValidationService';

    public generateLabel(scope: any, labelElement: angular.IAugmentedJQuery, validations: FieldMetadata, formName: string = 'editForm') {
        let formIsDirty = formName + '.$dirty';

        if (angular.isDefined(validations) && validations.required) {
            labelElement.append('<span class="cl-required-field" ng-show="' + formIsDirty + '">*&nbsp;</span>');
        }

        return labelElement;
    }

    /* element should be an input, textarea, select, etc */
    public generateValidation(scope: any, inputElement: angular.IAugmentedJQuery,
                              validations: FieldMetadata, formName: string = 'editForm') {
        /* We want to be able to handle the following situations:
        * 1. The validations do not exist and are not passed in (still build the input)
        * 2. The validations are passed in, but do not exist
        * (in cases where a component may be used within editable-content in one situation but not another)
        * So, dump out if we don't have them
        */
        if (!angular.isDefined(validations)) {
            return;
        }

        this.initFieldName(validations);

        let validationHandlers:
            Array<(elem: angular.IAugmentedJQuery, validations: FieldMetadata,
                   valMessages: angular.IAugmentedJQuery, pendingMessages?: angular.IAugmentedJQuery) => void> = [
                this.addRequiredVal,
                this.addLengthRangeVal,
                this.addMaxTextLengthVal,
                this.addRangeVal,
                this.addPatternVal,
                this.addEmailVal,
                this.addInvalidIfVal,
                this.addAsync
            ];

        let valMessages = this.initValMessages(inputElement, formName);
        let pendingMessages = this.initPendingMessages(inputElement, formName);

        validationHandlers.forEach((handler: (elem: angular.IAugmentedJQuery,
                                              validations: FieldMetadata,
                                              valMessages: angular.IAugmentedJQuery,
                                              pendingMessages?: angular.IAugmentedJQuery) => void) => {
            handler.call(this, inputElement, validations, valMessages, pendingMessages);
        });

        return valMessages.add(pendingMessages);
    }

    private addAsync(elem: angular.IAugmentedJQuery, validations: FieldMetadata,
                     valMessages: angular.IAugmentedJQuery, pendingMessages: angular.IAugmentedJQuery) {
        if (validations.async) {
            for (let key in validations.async) {
                let entry = validations.async[key];
                this.addValidationMessage(elem, [key], entry.errorMsg, valMessages);
                this.addPendingMessage(elem, [key], entry.pendingMsg, pendingMessages);
            }
        }
    }

    private addEmailVal(elem: angular.IAugmentedJQuery, validations: FieldMetadata, valMessages: angular.IAugmentedJQuery) {
        if (validations.email || false) {
            elem.attr('type', 'email');

            var message = validations.emailMsg || (validations.fieldName + 'Invalid');
            this.addValidationMessage(elem, ['email'], message, valMessages);
        }
    }

    private addInvalidIfVal(elem: angular.IAugmentedJQuery, validations: FieldMetadata, valMessages: angular.IAugmentedJQuery) {
        if (validations.invalidIf) {
            elem.attr('invalid-if', validations.invalidIf);

            var message = validations.invalidIfMsg || '';
            this.addValidationMessage(elem, ['invalidIf'], message, valMessages);
        }
    }

    private addMaxTextLengthVal(elem: angular.IAugmentedJQuery, validations: FieldMetadata) {
        if (validations.hasOwnProperty('maxTextLength') && validations.maxTextLength) {
            elem.attr('md-maxTextLength', validations.maxTextLength);
        }
    }

    private addLengthRangeVal(elem: angular.IAugmentedJQuery, validations: FieldMetadata, valMessages: angular.IAugmentedJQuery) {
        var isMinMsgSet = false;
        var isMaxMsgSet = false;
        var message;

        if (validations.hasOwnProperty('minLength') && validations.minLength >= 0) {
            elem.attr('ng-minlength', validations.minLength);

            if (validations.minLengthMsg) {
                isMinMsgSet = true;
                this.addValidationMessage(elem, ['minlength'], validations.minLengthMsg, valMessages);
            }
        }

        if (validations.hasOwnProperty('maxLength') && validations.maxLength > 0) {
            elem.attr('ng-maxlength', validations.maxLength);

            if (validations.maxLengthMsg) {
                isMaxMsgSet = true;
                this.addValidationMessage(elem, ['maxlength'], validations.maxLengthMsg, valMessages);
            }
        }

        if (!isMinMsgSet && !isMaxMsgSet && validations.minLength && validations.maxLength) {
            // tslint:disable-next-line: prefer-conditional-expression
            if (validations.minLength === validations.maxLength) {
                message = `Must have length of ${validations.minLength} characters`;
            } else {
                // tslint:disable-next-line: max-line-length
                message = validations.fieldName + 'Must have length between ' + validations.minLength + ' and ' + validations.maxLength + ' characters';
            }

            this.addValidationMessage(elem, ['minlength', 'maxlength'], message, valMessages);
            return;
        }

        if (!isMinMsgSet && validations.minLength) {
            message = validations.fieldName + 'Must be at least ' + validations.minLength + ' characters';

            this.addValidationMessage(elem, ['minlength'], message, valMessages);
            return;
        }

        if (!isMaxMsgSet && validations.maxLength) {
            message = validations.fieldName + 'Must be less than or equal ' + validations.maxLength + ' characters';

            this.addValidationMessage(elem, ['maxlength'], message, valMessages);
            return;
        }
    }

    private addPatternVal(elem: any, validations: FieldMetadata, valMessages: angular.IAugmentedJQuery) {
        if (validations.pattern) {
            elem.attr('ng-pattern', validations.pattern);

            var message = validations.patternMsg || (validations.fieldName + 'Invalid Value');
            this.addValidationMessage(elem, ['pattern'], message, valMessages);
        }
    }

    private addRangeVal(elem: angular.IAugmentedJQuery, validations: FieldMetadata, valMessages: angular.IAugmentedJQuery): void {
        var isMinMsgSet = false;
        var isMaxMsgSet = false;

        var isMinSet = validations.hasOwnProperty('min');
        var isMaxSet = validations.hasOwnProperty('max');

        if (isMinSet) {
            elem.attr(validations.useAngularMinMax ? 'ng-min' : 'min', validations.min);

            if (validations.minMsg) {
                isMinMsgSet = true;
                this.addCustomMinMessage(elem, validations, valMessages);
            }
        }

        if (isMaxSet) {
            elem.attr(validations.useAngularMinMax ? 'ng-max' : 'max', validations.max);

            if (validations.maxMsg) {
                isMaxMsgSet = true;
                this.addCustomMaxMessage(elem, validations, valMessages);
            }
        }

        if (!isMinMsgSet && !isMaxMsgSet && isMinSet && isMaxSet) {
            return this.addDefaultBetweenMessage(elem, validations, valMessages);
        }

        if (!isMinMsgSet && isMinSet) {
            return this.addDefaultMinMessage(elem, validations, valMessages);
        }

        if (!isMaxMsgSet && isMaxSet) {
            return this.addDefaultMaxMessage(elem, validations, valMessages);
        }
    }

    private addCustomMaxMessage(elem: angular.IAugmentedJQuery, validations: FieldMetadata, valMessages: angular.IAugmentedJQuery): void {
        this.addValidationMessage(elem, ['max'], validations.maxMsg, valMessages);
    }

    private addCustomMinMessage(elem: angular.IAugmentedJQuery, validations: FieldMetadata, valMessages: angular.IAugmentedJQuery): void {
        this.addValidationMessage(elem, ['min'], validations.minMsg, valMessages);
    }

    private addDefaultBetweenMessage(elem: angular.IAugmentedJQuery,
                                     validations: FieldMetadata, valMessages: angular.IAugmentedJQuery): void {
        var message = `${validations.fieldName} must be between ${validations.min} and ${validations.max}.`;

        this.addValidationMessage(elem, ['min', 'max'], message, valMessages);
    }

    private addDefaultMaxMessage(elem: angular.IAugmentedJQuery, validations: FieldMetadata, valMessages: angular.IAugmentedJQuery): void {
        var message = `${validations.fieldName} must be less than or equal to ${validations.max}.`;

        this.addValidationMessage(elem, ['max'], message, valMessages);
    }

    private addDefaultMinMessage(elem: angular.IAugmentedJQuery, validations: FieldMetadata, valMessages: angular.IAugmentedJQuery): void {
        var message = `${validations.fieldName} must be at least ${validations.min}.`;

        this.addValidationMessage(elem, ['min'], message, valMessages);
    }

    private addRequiredVal(elem: angular.IAugmentedJQuery, validations: FieldMetadata, valMessages: angular.IAugmentedJQuery) {
        if (validations.required || false) {
            elem.attr('ng-required', validations.required);
            elem.attr('md-no-asterisk', 'true');

            var message = validations.requiredMsg || (validations.fieldName + 'Required');
            this.addValidationMessage(elem, ['required'], message, valMessages);
        }
    }

    private addPendingMessage(elem: any, keys: string[], message: string, pendingMessages: angular.IAugmentedJQuery) {
        if (!keys || keys.length === 0) {
            return;
        }

        var pendingMessage = $('<div ng-message-exp=' + angular.toJson(keys) + '></div>');

        /* Fix the stupid, stupid, stupid issue with ngMessages, angular-material & angular-animate.
         * Do it with inline-styles instead of css classes to ensure specificity everywhere.
         * These styles are normally applied to all of these things anyway, and I don't know why it doesn't apply to some of them.
         * This may be able to be removed after upgrading angular-material again */
        pendingMessage.attr('style', 'opacity: 1; margin-top: 0;');

        pendingMessage.html(message);
        pendingMessages.append(pendingMessage);
    }

    private addValidationMessage(elem: any, keys: string[], message: string, valMessages: angular.IAugmentedJQuery) {
        if (!keys || keys.length === 0) {
            return;
        }

        var valMessage = $('<div ng-message-exp=' + angular.toJson(keys) + '></div>');

        /* Fix the stupid, stupid, stupid issue with ngMessages, angular-material & angular-animate.
         * Do it with inline-styles instead of css classes to ensure specificity everywhere.
         * These styles are normally applied to all of these things anyway, and I don't know why it doesn't apply to some of them.
         * This may be able to be removed after upgrading angular-material again */
        valMessage.attr('style', 'opacity: 1; margin-top: 0;');

        valMessage.html(message);
        valMessages.append(valMessage);
    }

    private initPendingMessages(elem: angular.IAugmentedJQuery, formName: string) {
        let formPrefix = formName + '.';

        let elemPendingKey = formPrefix + elem.attr('name') + '.$pending';

        let pendingMessages = $(`<div class="pending-validation" ng-messages="${elemPendingKey}" multiple></div>`);

        return pendingMessages;
    }

    private initValMessages(elem: angular.IAugmentedJQuery, formName: string) {
        let formPrefix = formName + '.';

        var elemErrorKey = formPrefix + elem.attr('name') + '.$error';

        var elemIsDirty = formPrefix + elem.attr('name') + '.$dirty';
        var elemIsInValid = formPrefix + elem.attr('name') + '.$invalid';
        var formIsSubmitted = formName + '.$submitted';

        var isUserInitiated = elemIsInValid + ' && (' + elemIsDirty + ' || ' + formIsSubmitted + ')';

        let valMessages = $('<div ng-messages="' + elemErrorKey + '" role="alert" multiple></div>');
        valMessages.attr('ng-show', isUserInitiated);

        return valMessages;
    }

    private initFieldName(validations: FieldMetadata) {
        validations.fieldName = validations.fieldName || '';

        if (validations.fieldName.length > 0) {
            validations.fieldName += ': ';
        }
    }
}
